import moment from "moment";
import { getErpBaseUrl, getErpToken } from "../erpClient";

/** Display Indian mobile from API digits (e.g. 919876543210 → +91 98765 43210). */
export function formatWhatsAppMobile(digits) {
  const raw = String(digits || "").replace(/\D/g, "");
  if (!raw) return "";

  if (raw.length === 12 && raw.startsWith("91")) {
    const local = raw.slice(2);
    return `+91 ${local.slice(0, 5)} ${local.slice(5)}`;
  }

  if (raw.length === 10) {
    return `+91 ${raw.slice(0, 5)} ${raw.slice(5)}`;
  }

  if (raw.length > 10) {
    const ccLen = raw.length - 10;
    const cc = raw.slice(0, ccLen);
    const local = raw.slice(ccLen);
    return `+${cc} ${local.slice(0, 5)} ${local.slice(5)}`;
  }

  return `+${raw}`;
}

export function formatRelativeTime(value) {
  if (!value) return "";
  const raw = String(value).trim();
  const m = /^\d{10}$/.test(raw)
    ? moment.unix(Number(raw))
    : moment(value);
  if (!m.isValid()) return "";
  const now = moment();
  if (now.diff(m, "minutes") < 1) return "Just now";
  if (now.isSame(m, "day")) return m.format("h:mm A");
  if (now.clone().subtract(1, "day").isSame(m, "day")) return "Yesterday";
  if (now.diff(m, "days") < 7) return m.format("ddd");
  return m.format("DD/MM/YY");
}

/** Compact phone for chat header: +916355054424 */
export function formatCompactWhatsAppMobile(digits) {
  const raw = String(digits || "").replace(/\D/g, "");
  if (!raw) return "";
  if (raw.length === 10) return `+91${raw}`;
  if (raw.length === 12 && raw.startsWith("91")) return `+${raw}`;
  return `+${raw}`;
}

export function getChatDisplayName(chat) {
  if (!chat) return "";
  return (
    String(chat.senderName || "").trim() ||
    formatWhatsAppMobile(chat.senderMobile || chat.chatId) ||
    String(chat.chatId || "")
  );
}

export function getInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export function getMessagePreviewText(message) {
  if (!message) return "";
  const body = message.body || {};
  const type = String(message.type || body.type || "text").toLowerCase();

  if (type === "text") {
    return body.text || message.preview || "";
  }
  if (type === "image") return body.caption ? `📷 ${body.caption}` : "📷 Photo";
  if (type === "video") return body.caption ? `🎥 ${body.caption}` : "🎥 Video";
  if (type === "audio" || type === "voice") return "🎵 Audio";
  if (type === "document") {
    const name =
      body.document?.filename || body.filename || body.caption || "";
    return name ? `📄 ${name}` : "📄 Document";
  }
  if (type === "location") return "📍 Location shared";
  if (type === "interactive") {
    return body.text || body.title || body.button_text || "Interactive message";
  }
  if (type === "template") {
    const name = body.template_name || body.name || "template";
    return `Template: ${name}`;
  }
  return body.text || message.preview || `[${type}]`;
}

/**
 * Build authenticated media proxy URL for <img>/<video>.
 * Path from API is usually relative (e.g. /api/whatsapp/.../media/...).
 * Appends ?token=<JWT> for browser requests (img cannot send Authorization header).
 */
export function resolveWhatsAppMediaUrl(pathOrUrl) {
  if (!pathOrUrl) return null;
  let url = String(pathOrUrl).trim();
  if (!url) return null;

  const token = getErpToken();
  const base = getErpBaseUrl();

  const appendTokenIfNeeded = (absoluteUrl) => {
    if (!token || /[?&]token=/.test(absoluteUrl)) return absoluteUrl;
    if (
      absoluteUrl.includes("/api/whatsapp/media/") ||
      absoluteUrl.includes("/whatsapp/media/")
    ) {
      return `${absoluteUrl}${absoluteUrl.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`;
    }
    return absoluteUrl;
  };

  if (/^https?:\/\//i.test(url)) {
    if (url.includes("/uploads/whatsapp_media/")) {
      return url;
    }
    return appendTokenIfNeeded(url);
  }

  if (url.startsWith("/api/") || url.startsWith("/quote/")) {
    const host = String(base).replace(/\/(api|quote)$/i, "");
    url = `${host}${url}`;
  } else if (url.startsWith("/")) {
    url = `${base}${url}`;
  } else {
    url = `${base}/${url}`;
  }

  return appendTokenIfNeeded(url);
}

export function getMessageMediaUrl(message) {
  if (!message) return null;
  const body = message.body || {};
  const type = getMessageType(message);
  const normalizedType = type === "voice" ? "audio" : type;

  const nested = body[normalizedType]?.link || body[normalizedType]?.url;
  if (nested) return resolveWhatsAppMediaUrl(nested);

  if (body.url) return resolveWhatsAppMediaUrl(body.url);
  if (message.url) return resolveWhatsAppMediaUrl(message.url);

  const mediaId =
    body.mediaId ||
    message.mediaId ||
    body[normalizedType]?.id ||
    body[normalizedType]?.mediaId;
  if (mediaId) {
    return resolveWhatsAppMediaUrl(`/whatsapp/media/${mediaId}`);
  }

  return resolveWhatsAppMediaUrl(body.mediaUrl || null);
}

export function getMessageCaption(message) {
  if (!message) return "";
  const body = message.body || {};
  const type = getMessageType(message);
  const normalizedType = type === "voice" ? "audio" : type;
  return String(
    body.caption || body[normalizedType]?.caption || "",
  ).trim();
}

export function getDocumentFilename(message) {
  if (!message) return "Document";
  const body = message.body || {};
  return (
    body.document?.filename ||
    body.filename ||
    body.document?.name ||
    "Document"
  );
}

export function getMessageType(message) {
  if (!message) return "text";
  const body = message.body || {};
  return String(message.type || body.type || "text").toLowerCase();
}

/** Normalize mobile for WhatsApp API (10-digit India → 91 prefix). */
export function normalizeWhatsAppMobileInput(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

export function validateWhatsAppMobileForSubmit(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return { ok: false, error: "Mobile number is required" };
  }
  const mobile = normalizeWhatsAppMobileInput(trimmed);
  if (mobile.length === 12 && mobile.startsWith("91")) {
    return { ok: true, mobile };
  }
  if (/^\d{11,15}$/.test(mobile)) {
    return { ok: true, mobile };
  }
  return {
    ok: false,
    error: "Enter a valid 10-digit Indian mobile or number with country code",
  };
}

function getMessageTimestamp(message) {
  const raw = message?.timestamp;
  if (raw == null || raw === "") return 0;
  const s = String(raw).trim();
  if (/^\d{10}$/.test(s)) return Number(s) * 1000;
  const parsed = Date.parse(s);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Oldest first — matches API chronological order; also fixes accidental DESC payloads. */
export function sortMessagesChronologically(messages) {
  return [...(messages || [])].sort((a, b) => {
    const diff = getMessageTimestamp(a) - getMessageTimestamp(b);
    if (diff !== 0) return diff;
    return (Number(a?.id) || 0) - (Number(b?.id) || 0);
  });
}
